import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { Ayuda } from '../components/Ayuda';
import { CargaPagina } from '../components/Spinner';
import { ConectarMicrosoft } from './NavegadorSharePoint';

/**
 * Panel de Control de AMBEC Plus (2026-10-22, ADR-055): tablero de gestión con las DIAs al centro.
 * Una sola llamada a GET /api/panel/resumen (permisos y bloque admin se resuelven en el servidor).
 * Nada se escribe desde aquí: cada pendiente enlaza a la pantalla donde se resuelve.
 */

interface DiaResumen { id: string; nombre: string; etapa: string | null; capitulosListos: number; capitulosTotal: number; ficha: { version: number; con: number; total: number; problemas: number } | null; pendientes: number; pendientesCortos: string[]; syncEstado: string; docsListos: number; docsTotal: number; ultimaActividad: { at: string; quien: string | null; que: string } | null; gastoMes: number; mia: boolean }
interface Pendiente { tipo: string; favoritoId: string; dia: string; detalle: string; enlace: string; mio: boolean }
interface Evento { at: string; favoritoId: string; dia: string; tipo: 'capitulo' | 'chat' | 'ficha'; detalle: string; quien: string | null }
interface Resumen {
  requiere_ms?: boolean;
  mes: string;
  cifras: { dias: number; capitulosMes: number; pendientes: number; gastoMes: number };
  cartera: DiaResumen[];
  mias: string[];
  pendientes: Pendiente[];
  actividad: Evento[];
  costos: { gastoDesde?: string | null; porDia: { favoritoId: string; nombre: string; usd: number }[]; admin?: { totalMes: number; totalMesAnterior: number; porAgente: { agente: string; usd: number; corridas: number }[] } };
  conocimiento: { referencias: number; consultasSeiaMes: number; observaciones: number; redactoresValidados: { ok: number; total: number } };
  newsletter: { ultimaEnviada: string | null; porAprobar: number; fuentesActivas: number; clientesActivos: number };
}

const usd = (n: number) => `USD ${n.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const nf = new Intl.NumberFormat('es-CL');
const rel = (iso: string) => {
  const d = (Date.now() - new Date(iso).getTime()) / 36e5;
  if (d < 1) return 'hace minutos';
  if (d < 24) return `hace ${Math.round(d)} h`;
  if (d < 48) return 'ayer';
  return new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
};
const MES = (m: string) => { const [y, mm] = m.split('-'); return new Date(Number(y), Number(mm) - 1, 1).toLocaleDateString('es-CL', { month: 'long', year: 'numeric' }); };
const NOMBRE_AGENTE: Record<string, string> = { A0: 'Memo', A1: 'Andrés', A2: 'Roberto', A3: 'Diamela', A4: 'Iris', A5: 'Eco', A6: 'Emilia', A7: 'Fernanda', A8: 'Tomás', A9: 'Camila', A10: 'Ignacio', A11: 'Valentina', A12: 'Matías', A13: 'Josefa', A14: 'Andrea', A15: 'Cristóbal', A16: 'Paula', A17: 'Rodrigo', A18: 'Sofía', A19: 'Auditora', A20: 'Elena', N1: 'Nicanor', N2: 'Marcela', N3: 'Violeta', N4: 'Gabriela', N5: 'Pablo' };

function Cifra({ titulo, valor, ayuda, alerta }: { titulo: string; valor: string; ayuda: string; alerta?: boolean }) {
  return (
    <section className="tarjeta p-4 sm:p-5">
      <p className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.14em] text-gris">{titulo}<Ayuda texto={ayuda} /></p>
      <p className={`mt-2 text-3xl font-medium leading-none tabular-nums ${alerta ? 'text-amber-700' : 'text-azul-profundo'}`}>{valor}</p>
    </section>
  );
}

function Bloque({ titulo, ayuda, accion, children, className = '' }: { titulo: string; ayuda: string; accion?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`tarjeta flex flex-col p-4 sm:p-5 ${className}`}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-1.5 text-sm font-medium text-azul-noche">{titulo}<Ayuda texto={ayuda} /></h2>
        {accion}
      </div>
      <div className="mt-3 flex-1">{children}</div>
    </section>
  );
}

function FilaCartera({ d, compacta = false }: { d: DiaResumen; compacta?: boolean }) {
  const pct = Math.round((d.capitulosListos / d.capitulosTotal) * 100);
  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:gap-4">
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-x-2 text-[13.5px] font-medium text-azul-noche">
          <span className="truncate"><span aria-hidden="true" className="mr-1 text-amber-400">★</span>{d.nombre}</span>
          <Ayuda texto={d.etapa ? 'Etapa del proyecto según la Ficha DIA (campo Estado).' : 'La etapa sale de la Ficha DIA (campo Estado). Sin ficha no se conoce.'}>
            <span className={`chip ${d.etapa ? 'bg-celeste/20 text-azul-profundo ring-celeste/60' : 'bg-slate-100 text-gris ring-slate-200'}`}>{d.etapa ?? 'sin etapa'}</span>
          </Ayuda>
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-gris">
          <Ayuda texto="Capítulos con borrador listo sobre los 14 de una DIA. Es producción interna (texto escrito), no la etapa del proyecto.">
            <span className="inline-flex items-center gap-2">
              <span aria-hidden="true" className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-100"><span className={`block h-full rounded-full ${pct === 100 ? 'bg-emerald-500' : 'bg-azul-medio'}`} style={{ width: `${pct}%` }} /></span>
              <span className="tabular-nums">{d.capitulosListos}/{d.capitulosTotal} capítulos</span>
            </span>
          </Ayuda>
          {d.ficha && (
            <Ayuda texto={`Campos de la Ficha DIA con valor: ${d.ficha.con} de ${d.ficha.total}.${d.ficha.problemas ? ` ${d.ficha.problemas} aviso(s) de validación.` : ''}`}><span className={`chip ${d.ficha.problemas ? 'bg-amber-50 text-amber-800 ring-amber-200' : 'bg-celeste/20 text-azul-profundo ring-celeste/60'}`}>Ficha {d.ficha.con}/{d.ficha.total}</span></Ayuda>
          )}
          {d.pendientesCortos.map((c, i) => (
            <Ayuda key={i} texto="Pendiente detectado por la plataforma. En 'Pendientes de atención' (más abajo) está el detalle y el enlace para resolverlo."><span className="chip bg-red-50 text-red-700 ring-red-200">⚠ {c}</span></Ayuda>
          ))}
          {!compacta && d.ultimaActividad && <span className="truncate">{rel(d.ultimaActividad.at)} · {d.ultimaActividad.que}{d.ultimaActividad.quien ? ` · ${d.ultimaActividad.quien}` : ''}</span>}
          {!compacta && d.gastoMes > 0 && <Ayuda texto="Gasto estimado en modelos de lenguaje de esta DIA en el mes (chats y capítulos), con tarifas oficiales."><span className="tabular-nums">{usd(d.gastoMes)} este mes</span></Ayuda>}
        </div>
      </div>
      <Link to={`/agentes/dia/${d.id}`} className="btn-primario !h-8 w-full text-center !text-[12.5px] sm:w-auto">Abrir</Link>
    </li>
  );
}

const ICONO: Record<string, string> = { sin_ficha: '📋', ficha_avisos: '⚠️', capitulo_desactualizado: '🔄', estudio_interrumpido: '⏸️', capitulo_error: '❌' };

export default function Panel() {
  const { esAdmin } = useAuth();
  const [r, setR] = useState<Resumen | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [soloMios, setSoloMios] = useState(false);
  useEffect(() => { apiFetch<Resumen>('/api/panel/resumen').then(setR).catch((e) => setError(e instanceof Error ? e.message : 'No se pudo cargar el panel.')); }, []);
  if (error) return <p role="alert" className="alerta-error">{error}</p>;
  if (!r) return <CargaPagina mensaje="Armando el tablero" />;
  if (r.requiere_ms) return <ConectarMicrosoft contexto="El tablero y los proyectos DIA funcionan" />;
  const mias = r.cartera.filter((d) => d.mia);
  const pendientes = soloMios ? r.pendientes.filter((p) => p.mio) : r.pendientes;
  return (
    <div className="flex flex-col gap-4">
      <p className="text-[12.5px] font-normal text-gris">Gestión de AMBEC Plus · {MES(r.mes)}<Ayuda texto="Tablero de gestión: la cartera de DIAs, lo que requiere atención, la actividad del equipo y los costos. El Newsletter aparece abajo como resumen." /></p>

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <Cifra titulo="DIAs en trabajo" valor={String(r.cifras.dias)} ayuda="Proyectos marcados como DIA en trabajo que puedes ver según tus permisos de SharePoint." />
        <Cifra titulo="Capítulos listos este mes" valor={String(r.cifras.capitulosMes)} ayuda="Borradores de capítulo generados y terminados en el mes en curso, en todas las DIAs visibles." />
        <Cifra titulo="Pendientes de atención" valor={String(r.cifras.pendientes)} alerta={r.cifras.pendientes > 0} ayuda="Fichas faltantes o con avisos, capítulos desactualizados o con error y estudios interrumpidos. La lista con enlaces está más abajo." />
        <Cifra titulo="Gasto del mes" valor={usd(r.cifras.gastoMes)} ayuda="Suma del costo estimado en modelos de lenguaje de las DIAs visibles (chats y capítulos) en el mes, con tarifas oficiales de Qwen." />
      </div>

      <Bloque titulo={`Cartera de DIAs ( ${r.cartera.length} )`} ayuda="Una fila por DIA: etapa (de la ficha), capítulos listos de 14, ficha, pendientes, última actividad y gasto del mes. Abrir lleva al proyecto." accion={<Link to="/agentes/dia" className="text-[12px] font-medium text-azul-profundo hover:underline">Agente DIA →</Link>}>
        {r.cartera.length === 0 ? <p className="py-6 text-center text-sm font-light text-gris">Aún no hay DIAs en trabajo. Agrégalas desde Agente DIA.</p> : (
          <ul className="divide-y divide-slate-100">{r.cartera.map((d) => <FilaCartera key={d.id} d={d} />)}</ul>
        )}
      </Bloque>

      <div className="grid gap-4 lg:grid-cols-2">
        <Bloque titulo={`Lo mío ( ${mias.length} )`} ayuda="Las DIAs donde conversaste con Andrés o generaste capítulos.">
          {mias.length === 0 ? <p className="py-4 text-sm font-light text-gris">Todavía no has trabajado en ninguna DIA. Abre una desde la cartera.</p> : (
            <ul className="divide-y divide-slate-100">{mias.map((d) => <FilaCartera key={d.id} d={d} compacta />)}</ul>
          )}
        </Bloque>
        <Bloque titulo={`Pendientes de atención ( ${pendientes.length} )`} ayuda="Detectados automáticamente: sin ficha, ficha con avisos, capítulo desactualizado por cambio de ficha, capítulo con error, estudio de carpeta interrumpido. Cada uno enlaza a donde se resuelve."
          accion={<label className="flex items-center gap-1.5 text-[12px] text-gris"><input type="checkbox" checked={soloMios} onChange={(e) => setSoloMios(e.target.checked)} /> solo míos</label>}>
          {pendientes.length === 0 ? <p className="py-4 text-sm font-light text-gris">Nada pendiente. 🎉</p> : (
            <ul className="flex flex-col gap-1.5">
              {pendientes.map((p, i) => (
                <li key={i}>
                  <Link to={p.enlace} className="flex items-start gap-2 rounded-lg px-2 py-1.5 text-[12.5px] text-texto hover:bg-slate-50">
                    <span aria-hidden="true">{ICONO[p.tipo] ?? '•'}</span>
                    <span className="min-w-0"><span className="font-medium text-azul-noche">{p.dia}</span> · {p.detalle} <span className="text-azul-profundo">→</span></span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Bloque>
      </div>

      <Bloque titulo="Actividad reciente" ayuda="Últimos capítulos generados, conversaciones con Andrés y fichas importadas, con quién y cuándo.">
        {r.actividad.length === 0 ? <p className="py-4 text-sm font-light text-gris">Sin actividad todavía.</p> : (
          <ul className="divide-y divide-slate-100">
            {r.actividad.map((e, i) => (
              <li key={i} className="flex flex-wrap items-baseline gap-x-2 py-1.5 text-[12.5px]">
                <span className="w-20 shrink-0 text-gris">{rel(e.at)}</span>
                <span aria-hidden="true">{e.tipo === 'capitulo' ? '📝' : e.tipo === 'chat' ? '💬' : '📋'}</span>
                <Link to={`/agentes/dia/${e.favoritoId}${e.tipo === 'capitulo' ? '?tab=capitulos' : e.tipo === 'ficha' ? '?tab=ficha' : ''}`} className="font-medium text-azul-noche hover:underline">{e.dia}</Link>
                <span className="min-w-0 flex-1 truncate text-texto">{e.detalle}</span>
                {e.quien && <span className="text-gris">{e.quien}</span>}
              </li>
            ))}
          </ul>
        )}
      </Bloque>

      <div className="grid gap-4 lg:grid-cols-2">
        <Bloque titulo="Agentes y costos" ayuda={`Gasto estimado en modelos de lenguaje con tarifas oficiales. Todos ven el gasto por DIA; el Administrador ve además el total del mes, el mes anterior y el detalle por agente.${r.costos.gastoDesde ? ` El contador se reinició el ${new Date(r.costos.gastoDesde).toLocaleDateString('es-CL')} (Admin → Setup LLMs → Tarifas y gasto).` : ''}`} accion={r.costos.gastoDesde ? <span className="text-[11px] text-gris">desde {new Date(r.costos.gastoDesde).toLocaleDateString('es-CL')}</span> : undefined}>
          <ul className="divide-y divide-slate-100 text-[12.5px]">
            {r.costos.porDia.map((c) => <li key={c.favoritoId} className="flex justify-between py-1.5"><span className="truncate text-texto">{c.nombre}</span><span className="tabular-nums text-azul-noche">{usd(c.usd)}</span></li>)}
            {r.costos.porDia.length === 0 && <li className="py-3 text-gris">Sin gasto registrado este mes.</li>}
          </ul>
          {esAdmin && r.costos.admin && (
            <div className="mt-3 rounded-xl bg-[#F6F9FD] p-3 text-[12.5px]">
              <p className="flex justify-between"><span className="text-gris">Total del mes (todos los agentes)</span><span className="font-medium tabular-nums text-azul-noche">{usd(r.costos.admin.totalMes)}</span></p>
              <p className="flex justify-between"><span className="text-gris">Mes anterior</span><span className="tabular-nums text-gris">{usd(r.costos.admin.totalMesAnterior)}</span></p>
              {r.costos.admin.porAgente.length > 0 && (
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {r.costos.admin.porAgente.slice(0, 8).map((a) => <li key={a.agente} className="chip bg-white text-texto ring-slate-200" title={`${a.corridas} corridas`}>{NOMBRE_AGENTE[a.agente] ?? a.agente} {usd(a.usd)}</li>)}
                </ul>
              )}
            </div>
          )}
        </Bloque>
        <Bloque titulo="Conocimiento" ayuda="Lo que entrena a los agentes: casos de referencia (DIAs terminadas y expedientes del SEA), consultas al portal e-SEIA del mes, observaciones de los servicios aprendidas y redactores validados en el banco de reconstrucción.">
          <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
            {[
              [nf.format(r.conocimiento.referencias), 'casos de referencia'],
              [nf.format(r.conocimiento.consultasSeiaMes), 'consultas e-SEIA este mes'],
              [nf.format(r.conocimiento.observaciones), 'observaciones de servicios aprendidas'],
              [`${r.conocimiento.redactoresValidados.ok}/${r.conocimiento.redactoresValidados.total}`, 'redactores validados'],
            ].map(([v, l]) => <div key={l} className="rounded-xl bg-slate-50 px-3 py-2.5"><dt className="text-[17px] font-medium tabular-nums text-azul-profundo">{v}</dt><dd className="text-[11px] font-light text-gris">{l}</dd></div>)}
          </dl>
        </Bloque>
      </div>

      <section className="tarjeta flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-[12.5px]">
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-texto">
          <span className="font-medium text-azul-noche">Newsletter</span>
          <Ayuda texto="Resumen del boletín regulatorio: última edición enviada, alertas pendientes de aprobar (7 días), fuentes activas y clientes." />
          <span>última enviada: {r.newsletter.ultimaEnviada ? rel(r.newsletter.ultimaEnviada) : 'ninguna'}</span>
          <span>· {r.newsletter.porAprobar} por aprobar</span>
          <span>· {r.newsletter.fuentesActivas} fuentes</span>
          <span>· {r.newsletter.clientesActivos} cliente{r.newsletter.clientesActivos === 1 ? '' : 's'}</span>
        </p>
        <Link to="/newsletter" className="btn-secundario !h-8 !text-[12px]">Ir al Newsletter →</Link>
      </section>
    </div>
  );
}
